'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Wordmark } from './Logo';
import { openCookieSettings } from '@/app/lib/cookieSettings';

interface FooterProps {
  forceShow?: boolean;
}

// Social profiles. Add the rest as accounts go live — drop in an entry with the
// live href and its icon path (keep the `sameAs` list in app/layout.tsx in sync
// so Google links the brand to each profile). Icons are single-path glyphs.
const socials: { label: string; href: string; path: string }[] = [
  {
    label: 'X (Twitter)',
    href: 'https://x.com/mcworldtools',
    path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/mcworldtools/',
    // Official Instagram glyph as a single path: rounded-square frame, lens
    // circle, and flash dot — drawn with evenodd so the inner counters knock out
    // rather than filling solid.
    path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z',
  },
  // Coming soon (add href + verified icon path when live):
  // YouTube, TikTok, Discord
];

export default function Footer({ forceShow = false }: FooterProps) {
  const pathname = usePathname();

  // Hide footer in layout on home page (it's included in the page content there)
  if (pathname === '/' && !forceShow) {
    return null;
  }

  return (
    <footer className="bg-zinc-900 border-t border-zinc-800 py-3 px-6 flex-shrink-0">
      <div className="max-w-7xl mx-auto">
        {/* One row only from xl. The three groups need ~760px side by side; from md (and, since the sidebar
            became a drawer below lg, at lg too) the footer is ~690–720px, so the disclaimer wrapped with its
            "·" hanging at the start of a line. Below xl the groups stack, centered. */}
        <div className="flex flex-col xl:flex-row justify-between items-center gap-4">

          {/* Links.
              Must wrap: at exactly 768px the parent used to switch to a row and put this beside the
              socials, and a fifth link (Terms) overflowed the viewport by 46px on EVERY route.
              Two nowrap groups, so a phone breaks BETWEEN them (About · FAQ · Contact / Privacy ·
              Terms) instead of leaving "Terms" alone on a second line. */}
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <span className="flex items-center gap-x-5 whitespace-nowrap">
            <Link
              href="/about"
              className="text-zinc-400 hover:text-white text-sm transition-colors"
            >
              About
            </Link>
            <Link
              href="/faq"
              className="text-zinc-400 hover:text-white text-sm transition-colors"
            >
              FAQ
            </Link>
            <Link
              href="/contact"
              className="text-zinc-400 hover:text-white text-sm transition-colors"
            >
              Contact Us
            </Link>
            </span>
            <span className="flex items-center gap-x-5 whitespace-nowrap">
            <Link
              href="/privacy"
              className="text-zinc-400 hover:text-white text-sm transition-colors"
            >
              Privacy Policy
            </Link>
            {/* /terms is in the sitemap but was linked from nowhere except the signup page, which
                is noindex and so passes no signal — an orphan. Sits beside Privacy Policy. */}
            <Link
              href="/terms"
              className="text-zinc-400 hover:text-white text-sm transition-colors"
            >
              Terms
            </Link>
            {/* Reopens both consent prompts (ours for analytics, Google's for ads in the UK/EEA/CH)
                so withdrawing consent is as easy as giving it. A button: it navigates nowhere.
                The label is Google's required wording for a site's own consent-revocation link
                (support.google.com/adsense/answer/10959060): keep it exactly. */}
            <button
              type="button"
              onClick={openCookieSettings}
              className="text-zinc-400 hover:text-white text-sm transition-colors"
            >
              Privacy and cookie settings
            </button>
            </span>
          </div>

          {/* Social links */}
          <div className="flex items-center gap-4">
            {socials.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="text-zinc-400 hover:text-emerald-400 transition-colors"
              >
                <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d={s.path} />
                </svg>
              </a>
            ))}
          </div>

          {/* Copyright */}
          {/* Phones: wordmark above the disclaimer, no separator. Side by side from sm, where both fit on one line. */}
          <div className="flex flex-col sm:flex-row items-center gap-x-2 gap-y-0.5 text-zinc-500 text-sm text-center">
            <Wordmark className="text-sm opacity-90" />
            <span className="whitespace-nowrap"><span aria-hidden="true" className="hidden sm:inline">· </span>Not affiliated with Mojang or Microsoft</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
